import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, createFeatureSelector } from '@ngrx/store';
import { EMPTY, merge, switchMap, take, withLatestFrom } from 'rxjs';
import { LOGGER } from '../shared/logger';
import { PERSISTED_SLICES } from './persisted-state';
import { StatePersistence } from './state-persistence';
import { StorageActions } from './storage.actions';

@Injectable()
export class StorageEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly slices = inject(PERSISTED_SLICES);
  private readonly persistence = inject(StatePersistence);
  private readonly log = inject(LOGGER).scope('storage');

  /**
   * Starts writing once hydration has reported back, and then keeps every registered slice
   * in sync under its own key. Effects are constructed before the app initializers run, so
   * an earlier subscription would save empty initial state over what is stored.
   *
   * The root state is captured alongside the action to give `persist` the exact value each
   * slice was hydrated with — that is what lets it recognise and drop the echo instead of
   * writing back what it just read.
   */
  readonly persistAll$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StorageActions.hydrated),
        take(1),
        withLatestFrom(this.store),
        switchMap(([, rootState]) => {
          const hydrated = rootState as Record<string, unknown>;
          if (this.slices.length === 0) return EMPTY;

          this.log.debug('watching slices for changes', {
            slices: this.slices.map(slice => slice.name),
          });
          return merge(
            ...this.slices.map(slice =>
              this.persistence.persist(
                slice.name,
                this.store.select(createFeatureSelector<unknown>(slice.name)),
                hydrated[slice.name],
              ),
            ),
          );
        }),
      ),
    { dispatch: false },
  );
}
